import { Alert, Button, Col, FormGroup, Input, Modal, ModalBody, ModalFooter, ModalHeader, Row } from "reactstrap";
import DateTime from "react-datetime";
import { useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import { Form, Formik, FormikProps } from "formik";
import toast from "react-hot-toast";
import { useMutation } from "@apollo/client";

import { updateCompanyPolicySchedule } from "../../graphql/company-policy/updateCompanyPolicySchedule";
import { addCompanyPolicySchedule } from "../../graphql/company-policy/addCompanyPolicySchedule";
import { removeCompanyPolicySchedule } from "../../graphql/company-policy/removeCompanyPolicySchedule";
import { useGetActivePolicies } from "../../hooks/company-policy/useCompanyPoliciesActive";

import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import FormError from "../../components/Shared/Forms/FormError";
import { CompanySchedulesFilterStatus, CompanySchedulesFilterStatusOptions, DateFormat, SelectOption } from "../../helper/constants";
import { HandleReformatDate, IsFutureOrToday } from "../../helper/Utilities";
import { CompanyPolicy } from "../../interfaces/models/CompanyPolicy";
import { useGetCompanyPolicySchedules } from "../../hooks/company-policy/useCompanyPolicySchedules";
import { CompanyPolicySchedule } from "../../interfaces/models/CompanyPolicySchedule";
import PromptDialog from "../../components/PromptDialog";
import AddCompanyPolicyScheduleValidator from "../../validators/AddCompanyPolicyScheduleValidator";
import RichFormSelect from "../../components/Shared/Forms/RichFormSelect";
import CompanyPolicySchedulesTable from "../../components/Compliance/CompanyPolicySchedulesTable";

interface FormData {
  _id: string;
  policy_id: string;
  policy?: CompanyPolicy
  start_date: string;
  end_date: string;
}

interface ModalParams {
  name: string;
  action: string;
}

interface FilterParams {
  search: string,
  status: SelectOption;
}


const CompanyPolicySchedulerPage = () => {
  const breadcrumbsData = [
    { name: "Complaince", path: `/compliance` },
    { name: "Policy Scheduler", path: `/compliance/policy-scheduler` },
  ];

  const today = moment().startOf('day').toDate();

  const { data, error, loading, refetch } = useGetCompanyPolicySchedules({
    filter: {
      start_date: today // Add a start date here to filter out upcoming dates
    }
  });

  const activePolicyRequest = useGetActivePolicies();

  const [addCompanyPolicyScheduleHook] = useMutation(addCompanyPolicySchedule, {
    onCompleted: () => {
      toast.success(`${modal.name} Policy Schedule was successful`);
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const [updateCompanyPolicyScheduleHook] = useMutation(updateCompanyPolicySchedule, {
    onCompleted: () => {
      toast.success(`${modal.name} Policy Schedule was successful`);
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const [removeCompanyPolicyScheduleHook] = useMutation(removeCompanyPolicySchedule, {
    onCompleted: () => {
      toast.success(`${modal.name} Policy Schedule was successful`);
    },
    onError: (err) => {
      toast.error(err.message);
    },
  });

  const [filter, setFilter] = useState<FilterParams>({
    search: '',
    status: CompanySchedulesFilterStatusOptions[0],
  });
  const [reloading, setIsReloading] = useState(false);
  
  const [schedules, setSchedules] = useState<CompanyPolicySchedule[]>([]);
  const [activePolicies, setActivePolicies] = useState<Partial<CompanyPolicy>[]>([]);

  const [isOnProcess, setIsOnProcess] = useState(false);

  const [modal, setModal] = useState<ModalParams>({
    name: "Add",
    action: "add",
  });
  const [isSchedulerOpen, setIsSchedulerOpen] = useState(false);
  const formikRef = useRef<FormikProps<FormData>>(null);
  const [initialValueSchedule, setInitialValueSchedule] = useState<FormData>({
    _id: "",
    policy_id: "",
    start_date: "",
    end_date: ""
  });
  const [selectedSchedule, setSelectedSchedule] = useState<SelectOption>(null);

  const [openDeleteScheduleModal, setOpenDeleteScheduleModal] = useState(false);

  const handleFilterStatus = async (status: number) => {

    setIsReloading(true);
    if (status === CompanySchedulesFilterStatus.ALL) {
      await refetch({
        filter: {}
      })
    }
    else if (status === CompanySchedulesFilterStatus.UPCOMING) {
      await refetch({
        filter: {
          start_date: today // Add a start date here to filter out upcoming dates
        }
      });
    }
    setIsReloading(false);

  }
  
  const schedulesResults = useMemo(() => {
    const search = filter.search;
    if (!search) return schedules.filter((item) => item.policy_details.name.includes(search));
    return schedules.filter((item) =>
      item.policy_details.name.toLowerCase().includes(search)
    );
  }, [filter.search, schedules]);

  useEffect(() => {
    if(activePolicyRequest.data && activePolicyRequest.data.company_policies) {
      const rawPolicies = activePolicyRequest.data.company_policies;
      setActivePolicies(rawPolicies);
    }
  },[activePolicyRequest.data]);

  useEffect(() => {
    if(data) {
      const raw = data.company_policy_schedules || [];
      setSchedules(raw);
    }
  },[data]);


  //#region Add/Edit

  const handleOpenSchedulerModal = () => {
    setInitialValueSchedule({
      _id: "",
      policy_id: "",
      start_date: moment().format("yyyy-MM-DD"),
      end_date: moment().add(7,'d').format("yyyy-MM-DD"),
    });
    setModal({
      name: "Add",
      action: "add",
    });
    setSelectedSchedule(null);
    setIsSchedulerOpen(true);
  }

  const handleOpenEditScheduleModal = (schedule: CompanyPolicySchedule) => {
    setInitialValueSchedule({
      _id: schedule._id,
      policy_id: schedule.policy_details._id,
      policy: schedule.policy_details,
      start_date: moment(schedule.start_date).format("yyyy-MM-DD"),
      end_date: moment(schedule.end_date).format("yyyy-MM-DD"),
    });
    setSelectedSchedule({
      label: schedule.policy_details.name,
      value: schedule.policy_details._id
    })
    setModal({
      name: "Update",
      action: "edit",
    });
    setIsSchedulerOpen(true);
  }

  const handleSubmitSchedule = async (value: FormData) => {

    if(isOnProcess) return;
    setIsOnProcess(true);

    let result = false;
    if(modal.action === "add") {
      result = await addCompanyPolicyScheduleHook({
        variables: {
          record: {
            policy_id: value.policy_id,
            start_date: new Date(value.start_date),
            end_date: new Date(value.end_date),
          }
        }
      })
        .then(res => (res.errors ? false : true))
        .catch(() => false);
    }
    else if(modal.action === "edit") {
      result = await updateCompanyPolicyScheduleHook({
        variables: {
          _id: value._id,
          record: {
            policy_id: value.policy_id,
            start_date: new Date(value.start_date),
            end_date: new Date(value.end_date),
          }
        }
      })
        .then(res => (res.errors ? false : true))
        .catch(() => false);
    }
    

    if(!result) {
      setIsOnProcess(false);
      return;
    }

    setIsSchedulerOpen(false);
    await handleFilterStatus(Number(filter.status.value));
    setIsOnProcess(false);

  }
  //#endregion

  //#region Delete Schedule
  const handleOpenDeleteScheduleModal = (schedule: CompanyPolicySchedule) => {
    setInitialValueSchedule({
      _id: schedule._id,
      policy_id: schedule.policy_id,
      start_date: moment(schedule.start_date).format("yyyy-MM-DD"),
      end_date: moment(schedule.end_date).format("yyyy-MM-DD"),
    });
    setModal({
      name: "Delete",
      action: "delete",
    })

    setOpenDeleteScheduleModal(true);
  }

  const handleDeleteSchedule = async () => {
    if(isOnProcess) return;

    setIsOnProcess(true);

    const value = initialValueSchedule;

    const result = await removeCompanyPolicyScheduleHook({
      variables: {
        _id: value._id,
      }
    })
      .then(res => (res.errors ? false : true))
      .catch(() => false);

    if(!result) {
      setIsOnProcess(false);
      return;
    }
    setOpenDeleteScheduleModal(false);
    await handleFilterStatus(Number(filter.status.value));
    setIsOnProcess(false);

  }

  //#endregion
  
  return (
    <>
      <Breadcrumbs crumbs={breadcrumbsData} />
      {error && <Alert color="danger">Oops, we’re having trouble getting the schedules. Please give it a minute, and if it doesn’t resolve, let us know.</Alert>}
      <div>
        <Row className="mb-1">
          <Col md={6} xs={12} className="mt-1">
              <Input 
                type="text" 
                placeholder="Search Policy names here"
                value={filter.search}
                onChange={(e) => { setFilter((prev) => ({...prev ,search: e.target.value})) }}
              />
          </Col>
          <Col md={4} xs={12} className="mt-1">
            <RichFormSelect
              value={filter.status}
              options={CompanySchedulesFilterStatusOptions}
              isSearchable={false}
              onChange={async (e) => {
                const status = e;
                setFilter((prev) => ({...prev, status: status}))
                await handleFilterStatus(Number(status.value))
              }}
            />
          </Col>
          <Col md={2} xs={12} className="mt-1">
            <Button color="primary" onClick={handleOpenSchedulerModal} className=" w-100">
              Add
            </Button>
          </Col>
        </Row>
        <div>
          <small>The policies listed here are time-based. When the date matches, only the scheduled policy’s nudges will apply.</small>
        </div>
        <CompanyPolicySchedulesTable
          loading={loading || reloading}
          hasCRUD={true}
          schedules={schedulesResults}
          handleOpenDeleteScheduleModal={handleOpenDeleteScheduleModal}
          handleOpenEditScheduleModal={handleOpenEditScheduleModal}
        />
      </div>

      <Modal id="scheduler-modal" backdrop={true} isOpen={isSchedulerOpen}>
          <ModalHeader toggle={() => setIsSchedulerOpen(prev => !prev)} className="pb-0">
            <p className="text-lg">{modal.name} Schedule</p>
          </ModalHeader>
          <ModalBody>
          <Formik
            innerRef={formikRef}
            initialValues={initialValueSchedule}
            validationSchema={AddCompanyPolicyScheduleValidator}
            onSubmit={async (value: FormData) => {
              await handleSubmitSchedule(value);
            }}
          >
            {({ errors, touched, setFieldValue, values }) => (
              <Form>
                <fieldset disabled={activePolicyRequest.loading}>
                  <FormGroup>
                    <label>Policy *</label>

                    {
                      values.policy_id !== "" && !activePolicies.find((u) => u._id === values.policy_id) ?
                        <>
                          <div className="d-flex justify-content-between">
                            <span className="description-details">{values.policy.name}</span>
                            <span role="button" className="text-primary" 
                              onClick={() => {
                                setFieldValue("policy_id", "");
                                setSelectedSchedule(null);
                              }}>
                                Change
                              </span>
                          </div>
                          <small className="text-muted">Policy is archived, but you can still update or change it.</small>
                        </>
                      :
                        <>
                          <RichFormSelect
                            options={activePolicies.map((item) => ({label: item.name, value: item._id}))}
                            onChange={(e: SelectOption) => { 
                              setFieldValue("policy_id", e.value);
                              setSelectedSchedule(e);
                            }}
                            value={selectedSchedule}
                            placeholder="Please Select a Policy"
                          />
                        </>
                      
                    }
                    
                    <FormError>{touched.policy_id && (errors.policy_id)}</FormError>
                  </FormGroup>
                  <FormGroup>
                    <label>Start Date *</label>
                    <DateTime 
                      className="w-100" 
                      onChange={(date) => {
                        const reformated = HandleReformatDate(date);
                        setFieldValue("start_date",reformated);
                      }}
                      isValidDate={IsFutureOrToday}
                      dateFormat={DateFormat} 
                      timeFormat={false}  
                      inputProps={{readOnly : true ,className:"form-control bg-white"}}
                      initialValue={new Date(values.start_date)}
                    />
                    <FormError>{errors.start_date}</FormError>
                  </FormGroup>
                  <FormGroup>
                    <label>End Date *</label>
                    <DateTime 
                      className="w-100" 
                      onChange={(date) => {
                        const reformated = HandleReformatDate(date);
                        setFieldValue("end_date",reformated)   
                      }} 
                      isValidDate={IsFutureOrToday}
                      dateFormat={DateFormat} 
                      timeFormat={false} 
                      inputProps={{readOnly : true ,className:"form-control bg-white"}} 
                      initialValue={new Date(values.end_date)}
                    />
                    <FormError>{touched.end_date && (errors.end_date)}</FormError>
                  </FormGroup>
                </fieldset>
              </Form>
            )}
          </Formik>
            
          </ModalBody>
          <ModalFooter>
            <Button disabled={isOnProcess} onClick={() => setIsSchedulerOpen((prev) => !prev)}> Close </Button>
            <Button disabled={isOnProcess} onClick={() => { formikRef.current.submitForm(); }} color="primary"> Save </Button>
          </ModalFooter>

        </Modal>
        <PromptDialog
          isOpen={openDeleteScheduleModal}
          onCancel={() => setOpenDeleteScheduleModal(false)}
          onConfirm={handleDeleteSchedule}
          title="Delete Schedule"
          promptMessage="Action is Irreversible, all related data of this schedule will be deleted."
          confirmBtnTxt="Delete"
          isOnProcess={isOnProcess}
        />
    </>
  );
};

export { CompanyPolicySchedulerPage as CompanyPolicyScheduler };
